(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  var revealObserver;
  var animationObserver;
  var pendingReveals = new Set();
  var animatedSections = new Map();
  var headerScrolled;
  var candleReturnFocus = null;
  var candleCloseTimer = null;

  // Continuous decoration runs only while visible. The bottle has its own
  // visibility-gated controller with cached layout measurements.
  function syncAnimation(section, visible) {
    var playing = visible && !document.hidden && !reduceMotion.matches;
    if (section.classList.contains('is-animating') !== playing) {
      section.classList.toggle('is-animating', playing);
    }
  }

  function initAnimations(root) {
    if (!('IntersectionObserver' in window)) return;
    if (!animationObserver) {
      animationObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (!animatedSections.has(entry.target)) return;
          animatedSections.set(entry.target, entry.isIntersecting);
          syncAnimation(entry.target, entry.isIntersecting);
        });
      });
    }
    (root || document).querySelectorAll('.bi-marquee').forEach(function (section) {
      if (animatedSections.has(section)) return;
      animatedSections.set(section, false);
      animationObserver.observe(section);
    });
  }

  function syncMotionPreference() {
    animatedSections.forEach(function (visible, section) { syncAnimation(section, visible); });
    if (!reduceMotion.matches) return;
    pendingReveals.forEach(function (item) {
      item.classList.add('is-visible');
      revealObserver.unobserve(item);
    });
    pendingReveals.clear();
  }

  function initReveals(root) {
    var scope = root || document;
    var items = scope.querySelectorAll('[data-bi-reveal]:not([data-bi-reveal-ready])');

    if (reduceMotion.matches || !('IntersectionObserver' in window)) {
      items.forEach(function (item) {
        item.dataset.biRevealReady = 'true';
        item.classList.add('is-visible');
      });
      return;
    }

    if (!items.length) return;
    if (!revealObserver) revealObserver = new IntersectionObserver(
      function (entries, currentObserver) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          currentObserver.unobserve(entry.target);
          pendingReveals.delete(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );

    items.forEach(function (item) {
      item.dataset.biRevealReady = 'true';
      pendingReveals.add(item);
      revealObserver.observe(item);
    });
  }

  function activateMood(tab, mood) {
    var tabs = Array.prototype.slice.call(mood.querySelectorAll('[data-bi-mood-button]'));
    var panels = Array.prototype.slice.call(mood.querySelectorAll('[data-bi-mood-panel]'));
    var controlledPanel = document.getElementById(tab.getAttribute('aria-controls'));

    tabs.forEach(function (item) {
      var active = item === tab;
      item.classList.toggle('is-active', active);
      item.setAttribute('aria-selected', active ? 'true' : 'false');
      item.setAttribute('tabindex', active ? '0' : '-1');
    });

    panels.forEach(function (panel) {
      panel.classList.remove('is-active');
      panel.hidden = true;
    });

    if (!controlledPanel) return;
    controlledPanel.hidden = false;
    window.requestAnimationFrame(function () {
      controlledPanel.classList.add('is-active');
    });
  }

  function initMoodSelectors(root) {
    var scope = root || document;
    scope.querySelectorAll('[data-bi-mood]:not([data-bi-mood-ready])').forEach(function (mood) {
      mood.dataset.biMoodReady = 'true';
      var tabs = Array.prototype.slice.call(mood.querySelectorAll('[data-bi-mood-button]'));

      tabs.forEach(function (tab, index) {
        tab.addEventListener('click', function () {
          activateMood(tab, mood);
        });

        tab.addEventListener('keydown', function (event) {
          if (event.key !== 'ArrowRight' && event.key !== 'ArrowDown' && event.key !== 'ArrowLeft' && event.key !== 'ArrowUp') {
            return;
          }

          event.preventDefault();
          var direction = event.key === 'ArrowRight' || event.key === 'ArrowDown' ? 1 : -1;
          var nextIndex = (index + direction + tabs.length) % tabs.length;
          tabs[nextIndex].focus();
          activateMood(tabs[nextIndex], mood);
        });
      });
    });
  }

  function getCandleModal() {
    return document.querySelector('[data-bi-candle-modal]');
  }

  function openCandleModal(trigger) {
    var modal = getCandleModal();
    if (!modal) return;

    window.clearTimeout(candleCloseTimer);
    candleReturnFocus = trigger || document.activeElement;
    modal.hidden = false;
    document.body.classList.add('bi-modal-open');

    window.requestAnimationFrame(function () {
      modal.classList.add('is-open');
      var dialog = modal.querySelector('[role="dialog"]');
      if (dialog) dialog.focus({ preventScroll: true });
    });
  }

  function closeCandleModal() {
    var modal = getCandleModal();
    if (!modal || modal.hidden) return;

    modal.classList.remove('is-open');
    document.body.classList.remove('bi-modal-open');
    candleCloseTimer = window.setTimeout(function () {
      modal.hidden = true;
    }, reduceMotion.matches ? 0 : 250);

    if (candleReturnFocus && typeof candleReturnFocus.focus === 'function') {
      candleReturnFocus.focus({ preventScroll: true });
    }
  }

  function initCandleModal() {
    if (document.documentElement.dataset.biCandleModalReady === 'true') return;
    document.documentElement.dataset.biCandleModalReady = 'true';

    document.addEventListener('click', function (event) {
      var opener = event.target.closest('[data-bi-candle-open]');
      if (opener) {
        event.preventDefault();
        openCandleModal(opener);
        return;
      }

      if (event.target.closest('[data-bi-candle-close]')) {
        event.preventDefault();
        closeCandleModal();
      }
    });

    document.addEventListener('keydown', function (event) {
      var modal = getCandleModal();
      if (!modal || modal.hidden) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        closeCandleModal();
        return;
      }

      if (event.key !== 'Tab') return;
      var dialog = modal.querySelector('[role="dialog"]');
      if (!dialog) return;
      var focusable = Array.prototype.slice.call(
        dialog.querySelectorAll('a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])')
      );
      if (!focusable.length) return;

      var first = focusable[0];
      var last = focusable[focusable.length - 1];
      if (event.shiftKey && (document.activeElement === first || document.activeElement === dialog)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    });
  }

  function updateHeader() {
    var scrolled = window.scrollY > 24;
    if (scrolled === headerScrolled) return;
    headerScrolled = scrolled;
    document.documentElement.classList.toggle('bi-header-scrolled', scrolled);
  }

  function initialize(root) {
    initAnimations(root);
    initReveals(root);
    initMoodSelectors(root);
    initCandleModal();
  }

  document.addEventListener('DOMContentLoaded', function () {
    initialize(document);
    document.documentElement.classList.add('bi-ready');
    updateHeader();
  });

  document.addEventListener('shopify:section:load', function (event) {
    initialize(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    animatedSections.forEach(function (_, section) {
      if (!event.target.contains(section)) return;
      animationObserver.unobserve(section);
      animatedSections.delete(section);
      syncAnimation(section, false);
    });
    pendingReveals.forEach(function (item) {
      if (!event.target.contains(item)) return;
      revealObserver.unobserve(item);
      pendingReveals.delete(item);
    });
  });

  window.addEventListener('scroll', updateHeader, { passive: true });
  document.addEventListener('visibilitychange', syncMotionPreference);

  if (typeof reduceMotion.addEventListener === 'function') {
    reduceMotion.addEventListener('change', syncMotionPreference);
  }
})();
